import { Injectable } from '@angular/core';
import { GlobalConstants } from './global-constants';

export interface ClientEncryptedPayload {
  encryptedMessage: string;
  encryptedKey: string;
  encryptedIv: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientCrypto {
  private publicKeyString: string = GlobalConstants.publicKeySPKI;
  private publicKey: CryptoKey | null = null;
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();

  private async getPublicKey(): Promise<CryptoKey | null> {
    if (this.publicKey) {
      return this.publicKey;
    }
    await this.importPublicKey(this.publicKeyString);
    return this.publicKey;
  }

  private async importPublicKey(pemKey: string): Promise<void> {
    const binaryDer: ArrayBuffer = this.pemToBinary(pemKey);
    this.publicKey = await crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256'
      },
      false,
      ['encrypt']
    );
  }

  private pemToBinary(pem: string): ArrayBuffer {
    const base64 = pem
      .replace(/-----BEGIN.*?-----/g, '')
      .replace(/-----END.*?-----/g, '')
      .replace(/\s/g, '');
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  generateAESKey(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  }

  generateAESIv(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(12));
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach(byte => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Ephemeral AES key/iv for a "get saved connection params" round-trip. The RSA-encrypted forms
   * are sent to the server (which re-encrypts the stored params with them); the base64 forms are
   * kept to `decryptAES()` the response. Mirrors the admin-dashboard connection-params flow — the
   * server RSA-decrypts to the *base64 string* and then base64-decodes to the raw key/iv bytes.
   */
  async prepareParamsDecryptionKeys(): Promise<{ encryptedKey: string; encryptedIv: string; aesKeyBase64: string; aesIvBase64: string }> {
    const aesKey = await this.generateAESKey();
    const iv = this.generateAESIv();
    const rawKey = await crypto.subtle.exportKey('raw', aesKey);
    const aesKeyBase64 = this.arrayBufferToBase64(rawKey);
    const aesIvBase64 = this.arrayBufferToBase64(iv.buffer as ArrayBuffer);
    const encryptedKey = await this.encryptRSA(aesKeyBase64);
    const encryptedIv = await this.encryptRSA(aesIvBase64);
    return { encryptedKey, encryptedIv, aesKeyBase64, aesIvBase64 };
  }

  /**
   * Encrypt into the single self-contained field the backend expects for connection params:
   * `base64(JSON.stringify({ EncryptedAESKey, EncryptedAESIv, EncryptedMessage }))`.
   * The server `ServerCrypto.decrypt()` base64-decodes and JSON-parses this ONE string, so the
   * whole envelope must live in `EncryptedConnectionParamsJson` (the separate EncryptedKey/Iv
   * columns are legacy and not read on decrypt). Mirrors the admin-dashboard `ClientCrypto.encrypt`.
   */
  async encryptCombined(payload: string): Promise<string> {
    const e = await this.encrypt(payload);
    return btoa(JSON.stringify({
      EncryptedAESKey: e.encryptedKey,
      EncryptedAESIv: e.encryptedIv,
      EncryptedMessage: e.encryptedMessage
    }));
  }

  /** Decrypt an AES-GCM base64 ciphertext with the given base64 key/iv (raw key bytes). */
  async decryptAES(messageBase64: string, aesKeyBase64: string, aesIvBase64: string): Promise<string> {
    const keyBuffer = this.base64ToArrayBuffer(aesKeyBase64);
    const aesKey = await crypto.subtle.importKey('raw', keyBuffer, { name: 'AES-GCM' }, false, ['decrypt']);
    const ivBuffer = this.base64ToArrayBuffer(aesIvBase64);
    const dataBuffer = this.base64ToArrayBuffer(messageBase64);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBuffer }, aesKey, dataBuffer);
    return this.decoder.decode(decrypted);
  }

  async encryptRSA(payload: string): Promise<string> {
    const publicKey = await this.getPublicKey();
    if (!publicKey) {
      throw new Error('Missing RSA public key');
    }
    const data = this.encoder.encode(payload);
    const encrypted = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      data
    );
    return this.arrayBufferToBase64(encrypted);
  }

  async encrypt(payload: string): Promise<ClientEncryptedPayload> {
    const publicKey = await this.getPublicKey();
    if (!publicKey) {
      throw new Error('Missing RSA public key');
    }

    const aesKey = await this.generateAESKey();
    const iv = this.generateAESIv();
    const messageBuffer = this.encoder.encode(payload);

    const ivCopy = new Uint8Array(iv);
    const encryptedMessage = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: ivCopy },
      aesKey,
      messageBuffer
    );

    const rawKeyBuffer = await crypto.subtle.exportKey('raw', aesKey);
    const encryptedKeyBuffer = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      rawKeyBuffer
    );
    const encryptedIvBuffer = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      ivCopy
    );

    return {
      encryptedMessage: this.arrayBufferToBase64(encryptedMessage),
      encryptedKey: this.arrayBufferToBase64(encryptedKeyBuffer),
      encryptedIv: this.arrayBufferToBase64(encryptedIvBuffer)
    };
  }
}
